import Collection from '@/components/helpers/DataDisplay/Collection'
import Ui from '@/classes/Ui'
import Pagination from '@/components/helpers/Layout/Pagination'
import Permission from '@/classes/Permission'

/*
  Using this mixin requires the follow configuration filled out in the components data:

  collectionMixinConfig: {
    storeResetPage: 'RESET_CURRENT_PAGE_PROJECTS', // Vuex store method to call when resetting page
    routerName: 'projects',                        // The name of the route in router.js
    emptyState: 'projects',                        // If an empty state should be set if no records are found
    default_filters: default_filters,              // passing the specific default filter as JSON
    default_sort_by: default_sort_by,              // Default sort by
    default_sort_order: default_sort_order         // Default sort order
  }
*/

export let collectionMixin = {
  components: { Collection, Pagination },
  data() {
    return {
      items: [],
      page: 1,
      total_pages: 1,
      params: {},
      level: 1,
      query: {},
      has_searched: false,
    }
  },
  computed: {
    singular_model() {
      return this.model.slice(0, -1)
    },
    add_button() {
      return this.button !== undefined ? this.button : this.singular_model
    },
    currentPage() {
      if (this.$route.name === 'projects') {
        return this.$store.getters.getCurrentPageProjects
      } else if (this.$route.name === 'segments') {
        return this.$store.getters.getCurrentPageSegments
      } else if (this.$route.name === 'project_leads') {
        return this.$store.getters.getCurrentPageLeads
      } else if (this.$route.name === 'users') {
        return this.$store.getters.getCurrentPageUsers
      } else if (this.$route.name === 'external_forms') {
        return this.$store.getters.getCurrentPageExternalForms
      } else if (this.$route.name === 'email_templates'){
        return this.$store.getters.getCurrentPageEmailTemplates
      } else if (this.$route.name === 'organizations'){
        return this.$store.getters.getCurrentPageOrganizations
      } else if (this.$route.name === 'lead_masks'){
        return this.$store.getters.getCurrentPageLeadMasks
      } else if (this.$route.name === 'workflows'){
        return this.$store.getters.getCurrentPageWorkflows
      }
    },
  },
  created(){
    this.initQuery()
  },
  mounted() {
    let permissions = Permission.loadPermissions()

    if (Permission.can(permissions, this.model + '.list')) {
      Ui.startLoading()
      Ui.setTitle({ label: this.title })
      Ui.setHeaderButtons([{
        name: this.add_button,
        action: this.model + ".new",
        classes: "btn-primary",
        icon: "",
        function: this.addButtonFunction,
      }])
      Ui.setPageLevel(this.level)
      Ui.setPageType(this.singular_model)
      this.retrieve()
    } else {
      this.$store.commit('ADD_ERROR', {
        type: 'warning',
        message: 'errors.unauthorized'
      })
      this.$router.push({ name: 'unauthorized' })
    }
  },
  methods: {
    itemDelete(item) {
      this.api.delete(item.id).then(() => {
        this.retrieve()
      })
    },
    goToPage(page) {
      if (this.page !== page) {
        this.query.page = page
        this.page = page
        Ui.setPageNumber(this.page)
        Ui.startLoading()
        this.retrieve()
      }
    },
    // Set an empty stage variable in Vuex which makes the application show an empty state instead of an empty list
    handleEmptyState() {
      if (this.collectionMixinConfig && "emptyState" in this.collectionMixinConfig) {
        this.$store.commit("SET_EMPTY_STATE", this.collectionMixinConfig["emptyState"])
      }
    },
    resetEmptyState() {
      this.$store.commit("SET_EMPTY_STATE", null)
    },
    generateRouterParams(data){
      let params = {id: this.id}
      let query = {}
      if(data.page != 1){
        query.page = data.page
      }
      if(data.search != ""){
        query.search = data.search
      }
      if(data.sort_by != this.default_sort_by){
        query.sort_by = data.sort_by
      }
      if(data.sort_order != this.default_sort_order){
        query.sort_order = data.sort_order
      }
      query.filter_by    = data.filter_by
      query.filter_value = data.filter_value

      if(data.filters){
        if(!_.isEqual(data.filters, this.collectionMixinConfig.default_filters)){
          query.filters = btoa(JSON.stringify(data.filters))
        }
      }
      return { name: this.collectionMixinConfig.routerName , query, params }
    },
    search(str){
      this.query.search = str
      this.query.page = 1
      let current_page = this.$route.name
      this.$store.commit(this.collectionMixinConfig.storeResetPage)
      let routerParams = this.generateRouterParams(this.query)
      this.$router.push(routerParams)
    },
    filterUpdate(filters){
      this.query.filters = filters
      let routerParams = this.generateRouterParams(this.query)
      this.$router.push(routerParams)
    },
    filterReset(){
      this.query.filters = null
      let routerParams = this.generateRouterParams(this.query)
      this.$router.push(routerParams)
    },
    sorting(params){
      this.query.sort_by = params.sort_by
      this.query.sort_order = params.sort_order
      let routerParams = this.generateRouterParams(this.query)
      this.$router.push(routerParams)
    },
    initQuery(){
      // when we do a search or we sort we add this.query information to the specific method(sort, filter etc...)
      if (this.collectionMixinConfig.default_sort_by) {
        this.query.sort_by = this.collectionMixinConfig.default_sort_by
        this.query.sort_order = this.collectionMixinConfig.default_sort_order
      }
      this.query.search = ""

      // from url
      let query = this.$route.query

      if(query.page){
        this.query.page = query.page
      }
      if(query.search){
        this.query.search = query.search
      }
      if(query.sort_by){
        this.query.sort_by = query.sort_by
      }
      if(query.sort_order){
        this.query.sort_order = query.sort_order
      }
      if(query.filter_by){
        this.query.filter_by = query.filter_by
      }
      if(query.filter_value){
        this.query.filter_value = query.filter_value
      }

      if(query.filters){
        this.query.filters = JSON.parse(atob(query.filters))
        this.filterDisplay = true
      } else {
        this.query.filters = JSON.parse(JSON.stringify(this.collectionMixinConfig.default_filters))
      }
    },
    async handleSearch() {
      await this.fetchCollection('search')
    },
    async handleFetchAll() {
      await this.fetchCollection()
    },
    async execFetch(method) {
      if (method === 'search') {
        return await this.api.search(this.query, this.query.search)
      } else {
        return await this.api.all(this.query)
      }
    },
    handleResourceNotFound() {
      // Don't show empty state if we are searching or filtering
      let isSearching = this.query.search !== '' || JSON.stringify(this.query.filters) !== JSON.stringify(this.collectionMixinConfig.default_filters)
      if(!isSearching) {
        this.handleEmptyState()
      }
    },
    async fetchCollection(method) {
      try {
        var items = await this.execFetch(method)
        // If results are not found we might have to do something specific
        if (('resources' in items && items.resources.length === 0) || items.length === 0) {
          this.handleResourceNotFound()
        }
      } catch (err) {
        throw err
      } finally {
        this.has_searched = true
        Ui.finishLoading()
        this.$store.commit("SET_IS_RETRIEVING", false)
      }
      this.items       = items.resources
      this.page        = items.current_page
      this.total_pages = items.total_pages
    },
    retrieve() {
      this.resetEmptyState()
      this.$store.commit("SET_IS_RETRIEVING", true)
      this.query.page = this.page
      this.has_searched = false
      if (this.query.search) {
        this.handleSearch()
      } else {
        this.handleFetchAll()
      }
    }
  },
  watch: {
    currentPage(val) {
      this.goToPage(val)
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/mixins/Collection.js